import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../src/contexts/AuthContext';
import { isContaSuspensa } from '../../src/lib/statusConta';
import MaintenanceScreen from '../../src/components/MaintenanceScreen';
import { formatCpfCnpj, cpfToPortalEmail } from './format';
import LogoMarcaPortal from './marca/LogoMarcaPortal';
import { useMarcaPortal, useTituloPortal } from './marca/MarcaPortalContext';
import {
    Mail,
    Lock,
    FileText,
    Receipt,
    Headset,
    AlertCircle,
    Loader2,
    ArrowRight,
    Leaf,
    IdCard,
    Info
} from 'lucide-react';

// Portal do Consumidor — página inicial pública (rota /portal-consumidor).
// Identidade visual da marca do portal (Vigor Energy, ou a da parceira em
// /portal-consumidor/gran — ver marca/marcaPortal.ts), NÃO do VigorHub.
// Acesso: NÃO há autocadastro. A conta é provisionada pelo time interno (edge
// function provisionar-consumidor-portal) a partir do contato com o suporte.
// Login: CPF do assinante + senha (inicial = 6 primeiros dígitos do CPF). A
// troca obrigatória em /portal-consumidor/nova-senha é decidida pelo
// PortalConsumidorGate, lendo consumidores.senha_provisoria_pendente — esta
// tela só faz login e redireciona pra /inicio.

const inputClass =
    'block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all';

const InputField = ({
    label,
    icon: Icon,
    ...props
}: { label: string; icon: React.ElementType } & React.InputHTMLAttributes<HTMLInputElement>) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
        <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon size={18} className="text-gray-400" />
            </div>
            <input className={inputClass} {...props} />
        </div>
    </div>
);

// Ícones dos 4 benefícios, na ordem dos textos da marca (marca.login.beneficios).
const ICONES_BENEFICIOS = [FileText, Receipt, Leaf, Headset];

const PortalConsumidorView = () => {
    const { user, profile, loading, signIn } = useAuth();
    const { marca } = useMarcaPortal();
    const EMAIL_SUPORTE = marca.contato.email;

    const [mode, setMode] = useState<'login' | 'acesso'>('login');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    // Conta inativada: o login é recusado, mas o consumidor só vê "manutenção"
    const [manutencaoEm, setManutencaoEm] = useState<Date | null>(null);

    const [cpf, setCpf] = useState('');
    const [password, setPassword] = useState('');

    useTituloPortal('Portal do Consumidor');

    const switchMode = (next: 'login' | 'acesso') => {
        setMode(next);
        setError(null);
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const docDigits = cpf.replace(/\D/g, '');
        if (docDigits.length !== 11 && docDigits.length !== 14) {
            setError('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.');
            return;
        }

        setSubmitting(true);
        try {
            const { error } = await signIn(cpfToPortalEmail(docDigits), password);
            if (error) {
                if (isContaSuspensa(error)) {
                    setManutencaoEm(new Date());
                } else {
                    setError(error.message === 'Invalid login credentials'
                        ? 'CPF/CNPJ ou senha incorretos. No primeiro acesso, a senha são os 6 primeiros números do seu CPF ou CNPJ.'
                        : error.message || 'Erro ao entrar.');
                }
            }
            // Sucesso: o AuthContext atualiza user/profile e o redirect abaixo assume
            // (PortalConsumidorGate decide se precisa trocar a senha primeiro).
        } catch {
            setError('Ocorreu um erro inesperado. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    if (manutencaoEm) {
        return (
            <MaintenanceScreen
                marca="portal"
                ultimaVerificacao={manutencaoEm}
                acao={{ label: 'Voltar', onClick: () => { setManutencaoEm(null); setPassword(''); } }}
            />
        );
    }

    // Colaborador/parceiro logado que caiu aqui por engano: aponta pro hub interno
    if (user && profile && profile.role !== 'consumidor') {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center space-y-4">
                    <img src="/LOGO-01.png" alt="Vigor Energy" className="h-20 mx-auto object-contain" />
                    <h1 className="text-xl font-bold text-gray-900">Esta é a área do consumidor</h1>
                    <p className="text-sm text-gray-500">
                        Você está conectado com uma conta interna ({profile.full_name}). O portal do consumidor é exclusivo para consumidores.
                    </p>
                    <Link
                        to="/"
                        className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-xl text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
                    >
                        Ir para o VigorHub
                        <ArrowRight size={18} />
                    </Link>
                </div>
            </div>
        );
    }

    // Consumidor autenticado → sempre /inicio; PortalConsumidorGate redireciona
    // pra /nova-senha se consumidores.senha_provisoria_pendente ainda for true.
    if (user && profile?.role === 'consumidor') {
        return <Navigate to="/portal-consumidor/inicio" replace />;
    }

    // Visitante — landing com login / como ter acesso
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col lg:flex-row">
            {/* Painel de marca */}
            <div className="lg:w-1/2 bg-gradient-to-br from-primary to-primary-dark text-white flex flex-col justify-center px-8 py-12 sm:px-14 lg:px-20">
                <LogoMarcaPortal variante="login" className="self-start mb-10 flex-wrap" />
                <h1 className="text-3xl sm:text-4xl font-bold leading-tight whitespace-pre-line">
                    {marca.login.titulo}
                </h1>
                <p className="mt-4 text-white/80 max-w-md">
                    {marca.login.subtitulo}
                </p>

                <div className="mt-10 space-y-5 max-w-md">
                    {marca.login.beneficios.map(({ titulo, descricao }, i) => {
                        const Icon = ICONES_BENEFICIOS[i] ?? FileText;
                        return (
                        <div key={titulo} className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                <Icon size={20} className="text-secondary" />
                            </div>
                            <div>
                                <p className="font-semibold">{titulo}</p>
                                <p className="text-sm text-white/70">{descricao}</p>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>

            {/* Card de acesso */}
            <div className="lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="p-8">
                        {/* Abas Entrar / Como ter acesso */}
                        <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
                            {([['login', 'Entrar'], ['acesso', 'Como ter acesso']] as const).map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => switchMode(value)}
                                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${mode === value
                                        ? 'bg-white text-primary shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {error && (
                            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3 text-sm animate-fade-in">
                                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {mode === 'login' ? (
                            <form onSubmit={handleLogin} className="space-y-5">
                                <div className="text-center mb-2">
                                    <h2 className="text-xl font-bold text-gray-900">Bem-vindo!</h2>
                                    <p className="text-sm text-gray-500 mt-1">Entre com o CPF ou CNPJ do assinante para acompanhar suas faturas.</p>
                                </div>
                                <InputField
                                    label="CPF ou CNPJ do assinante"
                                    icon={IdCard}
                                    type="text"
                                    required
                                    inputMode="numeric"
                                    value={cpf}
                                    onChange={(e) => setCpf(formatCpfCnpj(e.target.value))}
                                    placeholder="000.000.000-00"
                                />
                                <div>
                                    <InputField
                                        label="Senha"
                                        icon={Lock}
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                    />
                                    <p className="mt-2 text-xs text-gray-400 flex items-start gap-1.5">
                                        <Info size={14} className="mt-0.5 shrink-0" />
                                        No primeiro acesso, sua senha são os 6 primeiros números do seu CPF ou CNPJ. Você vai criar uma nova senha em seguida.
                                    </p>
                                </div>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl shadow-lg shadow-primary/25 text-sm font-bold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-70 disabled:cursor-not-allowed transition-all transform active:scale-[0.98]"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Entrando...
                                        </>
                                    ) : (
                                        <>
                                            Entrar
                                            <ArrowRight size={18} />
                                        </>
                                    )}
                                </button>
                                <p className="text-center text-sm text-gray-500">
                                    Ainda não tem acesso?{' '}
                                    <button type="button" onClick={() => switchMode('acesso')} className="font-semibold text-primary hover:text-primary/80">
                                        Saiba como obter
                                    </button>
                                </p>
                            </form>
                        ) : (
                            <div className="space-y-6 text-center">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
                                    <Headset size={30} className="text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">Fale com o nosso suporte</h2>
                                    <p className="text-sm text-gray-500 mt-2">
                                        O acesso ao portal é criado pelo nosso time. Envie um e-mail informando seu
                                        nome completo e o CPF do assinante da conta de energia, e nós liberamos o seu acesso.
                                    </p>
                                </div>
                                <a
                                    href={`mailto:${EMAIL_SUPORTE}?subject=${encodeURIComponent('Acesso ao Portal do Consumidor')}`}
                                    className="w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl shadow-lg shadow-primary/25 text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-all"
                                >
                                    <Mail size={18} />
                                    {EMAIL_SUPORTE}
                                </a>
                                <p className="text-xs text-gray-400">
                                    Após a liberação, entre com o CPF ou CNPJ do assinante e use os 6 primeiros números dele como senha inicial.
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-center">
                        <p className="text-xs text-gray-500">
                            {marca.login.rodape} Colaboradores acessam pelo{' '}
                            <Link to="/login" className="font-semibold text-primary hover:text-primary/80">VigorHub</Link>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalConsumidorView;
